'use client'

import { Check, X, Crown } from "lucide-react"
import { useLanguage } from "@/lib/i18n/language-provider"
import { ScrollReveal } from "@/components/scroll-reveal"
import { Button } from "@/components/ui/button"
import { useRouter } from "next/navigation"
import { useEffect, useRef } from "react"
import { captureEvent } from "@/lib/posthog-helper"

export function Comparison() {
  const { t } = useLanguage()
  const router = useRouter()
  const hasTrackedRef = useRef(false)

  // Track when comparison section is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedRef.current) {
            hasTrackedRef.current = true
            captureEvent('signup_flow:comparison_view', {
              scroll_depth: 'comparison_section',
            })
          }
        })
      },
      { threshold: 0.3 }
    )

    const section = document.querySelector('[data-comparison-section]')
    if (section) {
      observer.observe(section)
    }

    return () => observer.disconnect()
  }, [])

  const features = [
    {
      category: t('landing.comparison.grid.category'),
      items: [
        { name: t('landing.comparison.grid.patterns'), free: true, pro: true },
        { name: t('landing.comparison.grid.custom'), free: true, pro: true },
        { name: t('landing.comparison.grid.ledWall'), free: true, pro: true },
        { name: t('landing.comparison.grid.selfTest'), free: true, pro: true },
        { name: t('landing.comparison.grid.textsLogos'), free: true, pro: true },
        { name: t('landing.comparison.grid.sharedSessions'), free: false, pro: true, highlight: true },
      ]
    },
    {
      category: t('landing.comparison.display.category'),
      items: [
        { name: t('landing.comparison.display.external'), free: true, pro: true },
        { name: t('landing.comparison.display.fullscreen'), free: true, pro: true },
        { name: t('landing.comparison.display.hdExport'), free: true, pro: true },
      ]
    },
    {
      category: t('landing.comparison.multigrid.category'),
      items: [
        { name: t('landing.comparison.multigrid.canvas'), free: false, pro: true, highlight: true },
        { name: t('landing.comparison.multigrid.multiple'), free: false, pro: true, highlight: true },
        { name: t('landing.comparison.multigrid.animated'), free: false, pro: true, highlight: true },
        { name: t('landing.comparison.multigrid.resolution'), free: false, pro: true, highlight: true },
        { name: t('landing.comparison.multigrid.export'), free: false, pro: true, highlight: true },
      ]
    },
    {
      category: t('landing.comparison.synoptic.category'),
      items: [
        { name: t('landing.comparison.synoptic.projects'), free: false, pro: true, highlight: true },
        { name: t('landing.comparison.synoptic.workflow'), free: false, pro: true, highlight: true },
        { name: t('landing.comparison.synoptic.export'), free: false, pro: true, highlight: true },
        { name: t('landing.comparison.synoptic.library'), free: false, pro: true, highlight: true },
        { name: t('landing.comparison.synoptic.redundancy'), free: false, pro: true, highlight: true },
      ]
    },
    {
      category: t('landing.comparison.collaboration.category'),
      items: [
        { name: t('landing.comparison.collaboration.remote'), free: false, pro: true, highlight: true },
        { name: t('landing.comparison.collaboration.support'), free: false, pro: true },
        { name: t('landing.comparison.collaboration.beta'), free: false, pro: true },
      ]
    }
  ]

  return (
    <section className="py-16 md:py-32 relative overflow-hidden bg-muted/30" data-comparison-section>
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff12_2px,transparent_2px),linear-gradient(to_bottom,#ffffff12_2px,transparent_2px)] bg-[size:80px_80px]" />

      <div className="w-full relative z-10 px-4 md:px-6 flex flex-col items-center">
        <ScrollReveal animation="bounce-in" duration={1}>
          <div className="text-center mb-12 md:mb-20 max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-4 md:mb-6">
              {t('landing.comparison.title')}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed px-4">
              {t('landing.comparison.subtitle')}
            </p>
          </div>
        </ScrollReveal>

        <div className="w-full max-w-5xl">
          <ScrollReveal animation="fade-in" delay={0.2} duration={1}>
            {/* Table Header */}
            <div className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[2fr_1fr_1fr] gap-4 mb-4">
              <div className="hidden md:block" />
              <div className="border-2 border-foreground/20 p-4 bg-background text-center">
                <div className="text-lg md:text-xl font-bold mb-1">Free</div>
                <div className="text-sm text-muted-foreground">0€</div>
              </div>
              <div className="border-2 border-amber-500/40 p-4 bg-amber-500/5 text-center">
                <div className="text-lg md:text-xl font-bold mb-1 flex items-center justify-center gap-2">
                  <Crown className="w-4 h-4 md:w-5 md:h-5" />
                  Pro
                </div>
                <div className="text-sm text-muted-foreground">9,99€ / 6 mois</div>
              </div>
            </div>

            {/* Feature Groups */}
            {features.map((group, groupIndex) => (
              <div key={groupIndex} className="mb-8">
                <div className="mb-4 px-4 py-2 bg-foreground/5 border-l-4 border-foreground/20">
                  <h3 className="text-base md:text-lg font-bold">{group.category}</h3>
                </div>
                {group.items.map((item, itemIndex) => (
                  <div
                    key={itemIndex}
                    className={`grid grid-cols-[1fr_auto_auto] md:grid-cols-[2fr_1fr_1fr] gap-4 py-3 px-4 border-b border-foreground/10 hover:bg-foreground/5 transition ${
                      item.highlight ? 'bg-amber-500/5' : ''
                    }`}
                  >
                    <div className="flex items-center text-sm md:text-base">
                      {item.name}
                      {item.highlight && (
                        <span className="ml-2 px-2 py-0.5 bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 text-xs">
                          {t('landing.comparison.exclusive')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-center">
                      {item.free ? (
                        <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                      ) : (
                        <X className="w-5 h-5 text-muted-foreground/30" />
                      )}
                    </div>
                    <div className="flex items-center justify-center">
                      {item.pro ? (
                        <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                      ) : (
                        <X className="w-5 h-5 text-muted-foreground/30" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {/* CTA Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
              <div className="border-2 border-foreground/20 p-6 bg-background text-center">
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => router.push('/grid')}
                  className="w-full text-base px-8 py-6 h-auto"
                >
                  {t('landing.comparison.tryFree')}
                </Button>
                <p className="text-xs text-muted-foreground mt-3">
                  {t('landing.comparison.noCard')}
                </p>
              </div>
              <div className="border-2 border-amber-500/40 p-6 bg-amber-500/5 text-center">
                <Button
                  size="lg"
                  onClick={() => router.push('/pro')}
                  className="w-full text-base px-8 py-6 h-auto bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                >
                  <Crown className="mr-2 h-5 w-5" />
                  {t('landing.comparison.upgradePro')}
                </Button>
                <p className="text-xs text-muted-foreground mt-3">
                  {t('landing.comparison.cancelAnytime')}
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
